// Floating care-team button. Always present on member-area pages.
// Shows a peach pulse + unread count badge when there are unread physician
// messages. Hidden when the user is already inside the chat surface.
//
// Renders into document.body via a portal so that any transformed ancestor
// (e.g. AppShell's `.page-enter` fade-up animation) cannot become its
// containing block and pin it to the bottom of the page content.
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { C } from "@/lib/brand";

export default function CareTeamFloatingButton({ unreadCount = 0, consultationId }) {
  const location = useLocation();
  // Hide on chat & check-in surfaces themselves to avoid covering content.
  if (
    location.pathname.startsWith("/app/care-team") ||
    location.pathname.startsWith("/app/plan/messages") ||
    location.pathname.startsWith("/app/plan/checkin") ||
    location.pathname.startsWith("/app/check-in") ||
    location.pathname.startsWith("/app/assessment")
  ) {
    return null;
  }

  const target = consultationId ? `/app/care-team/${consultationId}` : "/app/plan/messages";
  const hasUnread = unreadCount > 0;

  if (typeof document === "undefined") return null;

  return createPortal(
    <Link
      to={target}
      data-testid="care-team-fab"
      aria-label={hasUnread ? `${unreadCount} unread messages from your care team` : "Message your care team"}
      className={hasUnread ? "ctfb ctfb-pulse" : "ctfb"}
      style={{
        position: "fixed",
        // Sit above the AppShell's mobile bottom-nav (≈64px) on mobile,
        // and lift comfortably above the safe-area inset on iPhones.
        bottom: "calc(80px + env(safe-area-inset-bottom))",
        right: 18,
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: C.ink,
        color: C.peach,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        textDecoration: "none",
        boxShadow: "0 8px 24px rgba(15,14,13,0.22)",
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      {hasUnread && (
        <span
          data-testid="care-team-fab-unread"
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            minWidth: 22,
            height: 22,
            padding: "0 6px",
            borderRadius: 999,
            background: C.peach,
            color: C.ink,
            fontSize: 11,
            fontWeight: 800,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${C.cream}`,
          }}
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </Link>,
    document.body,
  );
}
